import * as React from 'react';
import { Props } from '../Group';
import { Field, FieldProps } from '../../../../../../Passenger/components/Field/Field';
import { useTheme } from '../../../../../../theme';
import cn from 'classnames';
import { Collapse } from '@material-ui/core';
import { AdditionalTravellerFieldEnum } from '../../../../../../Passenger/types';
import { useFormState, useField } from 'react-final-form';
import { CdnIcon } from '@shared/ui';
import { useTranslation } from 'react-i18next';

// Компонент для отображения одного дополнительного поля с валидацией
const SubsidyFieldWithValidation: React.FC<FieldProps> = ({
	field,
	passengerId,
	passengerType,
	disabled,
	onTravellerSelect,
	selectedProfileId,
	passengerDocType
}) => {
	const { t } = useTranslation('Passenger');

	// Используем useField для получения состояния валидации из React Final Form
	const { SubsidyGroup: theme } = useTheme('PassengerForm');
	const { meta } = useField(field.name, { subscription: { touched: true, error: true, valid: true } });

	// Поле валидно, если нет ошибок и оно было затронуто (touched)
	const isValid = meta.touched && meta.valid && !meta.error;

	return (
		<div className={theme.fieldWrapper}>
			<div className={theme.fieldLabel}>
				<span className={theme.labelText}>{t('Enter your SNILS to check eligibility')}</span>
				{isValid && <CdnIcon icon="Info/Item Icluded" className={theme.validIcon} />}
			</div>

			<Field
				field={field}
				passengerId={passengerId}
				passengerType={passengerType}
				disabled={disabled}
				onTravellerSelect={onTravellerSelect}
				selectedProfileId={selectedProfileId}
				passengerDocType={passengerDocType}
			/>
		</div>
	);
};

const SubsidiesGroup: React.FC<Props> = props => {
	const { SubsidyGroup: theme, Group: groupTheme } = useTheme('PassengerForm');
	const { t } = useTranslation('Passenger');

	const { values } = useFormState({ subscription: { values: true } });

	// Разделяем поля на основное поле subsidy и дополнительные поля
	const subsidyField = props.fields.find(field => field.type === AdditionalTravellerFieldEnum.Subsidy);
	const additionalFields = props.fields.filter(field => field.isSubsidyAdditionalField);

	// Проверяем, выбрана ли субсидия
	const subsidyValue = values?.passengers?.[props.passengerId]?.subsidy;
	const isSubsidySelected = !!subsidyValue;

	return (
		<div
			className={cn(groupTheme.container, theme.container, {
				[groupTheme.withIcons]: !!props.groupIcon
			})}
		>
			{props.groupIcon && <div className={groupTheme.icon}>{props.groupIcon}</div>}

			<h4 className={theme.title}>
				{t('I agree to verify the application of the subsidized fare:', { nsSeparator: '|' })}
			</h4>

			{/* Основное поле выбора субсидии */}
			{subsidyField && (
				<Field
					field={subsidyField}
					passengerId={props.passengerId}
					passengerType={props.passengerType}
					disabled={subsidyField.readonly || props.disabled}
					onTravellerSelect={props.onTravellerSelect}
					selectedProfileId={props.selectedProfileId}
					passengerDocType={props.passengerDocType}
				/>
			)}

			{/* Дополнительные поля субсидии в Collapse */}
			<Collapse in={isSubsidySelected} className={theme.collapse}>
				<div className={theme.additionalFields}>
					{additionalFields.map((field, index) => (
						<SubsidyFieldWithValidation
							key={`${field.name}_${index}`}
							field={field}
							passengerId={props.passengerId}
							passengerType={props.passengerType}
							disabled={field.readonly || props.disabled}
							onTravellerSelect={props.onTravellerSelect}
							selectedProfileId={props.selectedProfileId}
							passengerDocType={props.passengerDocType}
						/>
					))}
				</div>
			</Collapse>
		</div>
	);
};

export default SubsidiesGroup;
